import { useState, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Modal, Spin, DatePicker } from "antd";
import { useOrderingMessage } from "../../useOrderingMessage";
import { useTranslation } from "react-i18next";
import { posModalStyles as modalStyles } from "../../modalPrimitives";
import {
  CheckOutlined,
  LoadingOutlined,
  UserOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import {
  useGeneratePersonalReport,
  usePreviewPersonalReport,
} from "../../../../../hooks/useReports";
import { useUsers } from "../../../../../hooks/useUsers";
import { usePosPrintersExist } from "../../../../../hooks/useDevices";
import { printPersonalReport, type Report } from "../../../../../api/reports";
import { PersonalReportPreviewModal } from "./PersonalReportPreviewModal";
import { PrintPosConfirmModal } from "./PrintPosConfirmModal";
import {
  getReportModalWidth,
  getUserListColumns,
  userListGridClass,
  userListMaxHeightClass,
  userListScrollClass,
} from "./userListLayout";
import { gridArrowIndex, isArrowKey, scrollMarkedIntoView, takeKeyFromField } from "../listKeyboard";
import dayjs, { Dayjs } from "dayjs";
import { getApiErrorMessage } from "../../../../../utils/translations";
import { useViewport } from "../../../../../hooks/useViewport";
import { usePrinterNotifications } from "../../../../../hooks/usePrinterNotifications";
import { useAppSelector } from "../../../../../store/hooks";
import { R } from "../../orderingTheme";
import {
  ModalHeader,
  ModalFooter,
  GhostButton,
  PrimaryButton,
} from "../../modalPrimitives";

interface GeneratePersonalReportModalProps {
  open: boolean;
  onClose: () => void;
}

export const GeneratePersonalReportModal = ({
  open,
  onClose,
}: GeneratePersonalReportModalProps) => {
  const { t } = useTranslation();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  // Продаваемото е асортимент, а в режим „със склад" — рецепта. Същото
  // разклонение като `useAssortmentReportTitleKey` в справките.
  const usesWarehouse = useAppSelector(
    (s) => s.auth.user?.uses_warehouse === true,
  );
  const previewMutation = usePreviewPersonalReport();
  const generateMutation = useGeneratePersonalReport();
  const message = useOrderingMessage();
  const { notifyPrinterError } = usePrinterNotifications();
  const queryClient = useQueryClient();
  // Свежи при всяко отваряне: включването на заявката (`enabled`) тегли само
  const { data: users = [], isLoading: isLoadingUsers } = useUsers(undefined, true, undefined, {
    enabled: open,
  });
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  // Секцията с продаденото е по избор и по подразбиране е ИЗКЛЮЧЕНА. Изборът
  // важи за самия документ: при изключен чекбокс BE-то не смята и не записва
  // секцията, значи отчетът я няма и после в Справки, и на POS билета.
  const [includeSoldSellables, setIncludeSoldSellables] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const hasLoadedFirstDateRef = useRef(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [generatedReport, setGeneratedReport] = useState<Report | null>(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isCreatingReport, setIsCreatingReport] = useState(false);
  const [showPrintPosConfirm, setShowPrintPosConfirm] = useState(false);
  const [isPrintingPos, setIsPrintingPos] = useState(false);
  const [lastReportId, setLastReportId] = useState<number | null>(null);
  // Ако заведението няма нито един POS принтер, въпросът „Печат от POS
  // принтер?" се прескача изцяло. При неуспешна заявка (default true) питаме
  // както досега — безопасният вариант.
  const { data: posPrintersExist = true } = usePosPrintersExist(open);

  useEffect(() => {
    if (users.length === 1 && !selectedUserId) {
      setSelectedUserId(users[0].id);
    }
  }, [users, selectedUserId]);

  useEffect(() => {
    if (!open) {
      setSelectedUserId(null);
      setSelectedDate(dayjs());
      setIncludeSoldSellables(false);
      setIsGenerating(false);
      hasLoadedFirstDateRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    const loadFirstOrderDate = async () => {
      if (!open || hasLoadedFirstDateRef.current) return;
      if (users.length === 0) return;
      const firstUser = users[0];
      try {
        const report = await previewMutation.mutateAsync({
          user_id: firstUser.id,
          user_name: firstUser.names ?? firstUser.username,
          report_date: dayjs().toISOString(),
        });
        if (report.first_order_time) {
          setSelectedDate(dayjs(report.first_order_time));
        } else {
          setSelectedDate(dayjs());
        }
        hasLoadedFirstDateRef.current = true;
      } catch (error) {
        console.error("Error loading first order date:", error);
        setSelectedDate(dayjs());
      }
    };
    loadFirstOrderDate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, users]);

  // Колоните на списъка (userListLayout.ts) — за стрелките и за ширината долу.
  const userListColumns = getUserListColumns(users.length, isNarrow);
  const userListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || !isDesktop) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (showPreview || showConfirm) return;
      if (e.key === "Enter") {
        e.preventDefault();
        if (selectedUserId && !isGenerating) handlePreviewReport();
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (isArrowKey(e.key) && takeKeyFromField(e)) {
        // Решетката със сервитьорите — по общите правила (`listKeyboard.ts`);
        // в полето за дата стрелките са на календара.
        e.preventDefault();
        if (showPrintPosConfirm || users.length === 0) return;
        const index = users.findIndex((user) => user.id === selectedUserId);
        setSelectedUserId(users[gridArrowIndex(e.key, index, users.length, userListColumns)].id);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    selectedUserId,
    isGenerating,
    showPreview,
    showConfirm,
    showPrintPosConfirm,
    users,
    userListColumns,
    onClose,
    isDesktop,
  ]);

  // Маркираният — във видимата част на списъка.
  useEffect(() => {
    scrollMarkedIntoView(userListRef.current, `[data-user-id="${selectedUserId}"]`);
  }, [selectedUserId]);

  const handlePreviewReport = async () => {
    if (!selectedUserId) {
      message.warning(t("generateReport.selectUserWarning"));
      return;
    }
    const selectedUser = users.find((u) => u.id === selectedUserId);
    if (!selectedUser) return;
    setIsGenerating(true);
    try {
      const report = await previewMutation.mutateAsync({
        user_id: selectedUserId,
        user_name: selectedUser.names ?? selectedUser.username,
        report_date: selectedDate.toISOString(),
        include_sold_sellables: includeSoldSellables,
      });
      setGeneratedReport(report as unknown as Report);
      setIsPreviewMode(true);
      setShowPreview(true);
    } catch (error: unknown) {
      const errorMessage = getApiErrorMessage(
        error,
        t("generateReport.previewError"),
      );
      message.error(errorMessage);
      console.error("Error previewing report:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const showConfirmation = () => {
    setShowPreview(false);
    setShowConfirm(true);
  };
  const handleConfirmOk = async () => {
    setShowConfirm(false);
    await handleGenerateReport();
  };
  const handleConfirmCancel = () => setShowConfirm(false);

  const handleGenerateReport = async () => {
    if (!selectedUserId) return;
    const selectedUser = users.find((u) => u.id === selectedUserId);
    if (!selectedUser) return;
    setIsCreatingReport(true);
    try {
      const created = await generateMutation.mutateAsync({
        user_id: selectedUserId,
        user_name: selectedUser.names ?? selectedUser.username,
        report_date: selectedDate.toISOString(),
        include_sold_sellables: includeSoldSellables,
      });
      setGeneratedReport(null);
      setIsPreviewMode(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["users"] }),
        queryClient.invalidateQueries({ queryKey: ["bills"] }),
      ]);
      message.success(t("generateReport.reportCreated"));
      setLastReportId(typeof created?.id === "number" ? created.id : null);
      if (posPrintersExist) {
        setShowPrintPosConfirm(true);
      } else {
        onClose();
      }
    } catch (error: unknown) {
      const errorMessage = getApiErrorMessage(
        error,
        t("generateReport.generateError"),
      );
      message.error(errorMessage);
      console.error("Error generating report:", error);
    } finally {
      setIsCreatingReport(false);
    }
  };

  const handlePrintPosNo = () => {
    setShowPrintPosConfirm(false);
    onClose();
  };

  const handlePrintPosYes = async () => {
    if (lastReportId == null) {
      setShowPrintPosConfirm(false);
      onClose();
      return;
    }
    setIsPrintingPos(true);
    try {
      await printPersonalReport(lastReportId);
      message.success(t("personalReport.printPosSuccess"));
    } catch (error: unknown) {
      notifyPrinterError(
        getApiErrorMessage(error, t("personalReport.printPosError")),
      );
    } finally {
      setIsPrintingPos(false);
      setShowPrintPosConfirm(false);
      onClose();
    }
  };

  const handlePreviewClose = () => {
    setShowPreview(false);
    setGeneratedReport(null);
    setIsPreviewMode(false);
    onClose();
  };

  // При много потребители списъкът минава в 2/3 колони и модалът се
  // разширява с тях (userListLayout.ts).
  const width = getReportModalWidth(userListColumns, isMobile, isTablet);
  const bodyPad = isNarrow ? "14px 16px" : "18px 24px";

  return (
    <>
      <PrintPosConfirmModal
        open={showPrintPosConfirm}
        onYes={handlePrintPosYes}
        onNo={handlePrintPosNo}
        loading={isPrintingPos}
      />

      {/* Confirm reset modal */}
      <Modal
        open={showConfirm}
        onCancel={handleConfirmCancel}
        footer={null}
        centered
        closable={false}
        destroyOnHidden
        width={isMobile ? undefined : 420}
        styles={modalStyles}
        style={{ maxWidth: "calc(100vw - 32px)" }}
        zIndex={2000}
      >
        <div
          style={{ background: R.surface, fontFamily: R.font, color: R.text }}
        >
          <ModalHeader
            icon={<ExclamationCircleOutlined />}
            title={t("generateReport.resetReport")}
            subtitle={t("generateReport.resetConfirmDescription")}
            onClose={handleConfirmCancel}
            isNarrow={isNarrow}
          />
          <ModalFooter isNarrow={isNarrow} narrowFirst>
            <GhostButton
              label={t("common.cancel")}
              onClick={handleConfirmCancel}
            />
            <PrimaryButton
              label={t("generateReport.resetButton")}
              onClick={handleConfirmOk}
              loading={isCreatingReport}
            />
          </ModalFooter>
        </div>
      </Modal>

      <PersonalReportPreviewModal
        open={showPreview}
        onClose={handlePreviewClose}
        report={generatedReport}
        isPreviewMode={isPreviewMode}
        onConfirmCreate={showConfirmation}
        isCreating={isCreatingReport}
      />

      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        centered
        closable={false}
        destroyOnHidden
        width={width}
        styles={modalStyles}
        style={{ maxWidth: "calc(100vw - 32px)" }}
      >
        <div
          style={{ background: R.surface, fontFamily: R.font, color: R.text }}
        >
          <ModalHeader
            icon={<FileTextOutlined />}
            title={t("generateReport.title")}
            onClose={onClose}
            isNarrow={isNarrow}
          />

          {isLoadingUsers ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "48px 24px",
              }}
            >
              <Spin
                indicator={
                  <LoadingOutlined
                    style={{ fontSize: 36, color: R.brand }}
                    spin
                  />
                }
              />
            </div>
          ) : (
            <>
              <div
                style={{
                  padding: bodyPad,
                  display: "flex",
                  flexDirection: "column",
                  gap: 14,
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      letterSpacing: 1.2,
                      color: R.textMuted,
                      textTransform: "uppercase",
                      marginBottom: 6,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    {t("generateReport.selectUser")}
                    {users.length === 0 && !isLoadingUsers && (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 500,
                          color: R.textMuted,
                          letterSpacing: 0,
                          textTransform: "none",
                          fontStyle: "italic",
                        }}
                      >
                        — {t("generateReport.noUsersAvailable")}
                      </span>
                    )}
                  </div>
                  <div
                    ref={userListRef}
                    className={`grid gap-1.5 ${userListScrollClass} ${userListMaxHeightClass} ${userListGridClass[userListColumns]}`}
                  >
                    {users.map((user) => {
                      const isSelected = selectedUserId === user.id;
                      return (
                        <button
                          key={user.id}
                          type="button"
                          aria-pressed={isSelected}
                          data-user-id={user.id}
                          onClick={() => setSelectedUserId(user.id)}
                          className={`flex cursor-pointer items-center justify-between gap-2 rounded-[10px] border px-3 py-2.5 text-left transition-colors duration-[120ms] ${
                            isSelected
                              ? "border-pos-accent/30 bg-pos-ready-bg"
                              : "border-pos-line2 bg-pos-card"
                          }`}
                        >
                          <span className="inline-flex min-w-0 flex-1 items-center gap-2">
                            <UserOutlined
                              className={`flex-shrink-0 text-[13px] ${
                                isSelected
                                  ? "text-pos-accent-ink"
                                  : "text-pos-muted"
                              }`}
                            />
                            <span
                              className={`overflow-hidden text-ellipsis whitespace-nowrap text-[13.5px] font-semibold ${
                                isSelected
                                  ? "text-pos-accent-ink"
                                  : "text-pos-ink"
                              }`}
                            >
                              {user.names || user.username}
                            </span>
                          </span>
                          {user.unreported_bills_amount !== undefined &&
                            user.unreported_bills_amount > 0 && (
                              <span
                                className={`flex-shrink-0 rounded-full border px-2 py-[3px] font-pos-mono text-[13px] font-extrabold tracking-[-0.2px] text-pos-accent-ink ${
                                  isSelected
                                    ? "border-pos-accent/30 bg-pos-card"
                                    : "border-pos-accent/25 bg-pos-ready-bg"
                                }`}
                              >
                                {user.unreported_bills_amount.toFixed(2)}
                              </span>
                            )}
                          <span
                            className={`inline-flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center rounded-full border text-white ${
                              isSelected
                                ? "border-pos-accent bg-pos-accent"
                                : "border-pos-ctl bg-pos-card"
                            }`}
                          >
                            {isSelected && (
                              <CheckOutlined className="text-[10px]" />
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <div
                    style={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      letterSpacing: 1.2,
                      color: R.textMuted,
                      textTransform: "uppercase",
                      marginBottom: 6,
                    }}
                  >
                    {t("generateReport.reportDate")}
                  </div>
                  <DatePicker
                    value={selectedDate}
                    onChange={(date) => date && setSelectedDate(date)}
                    format="DD.MM.YYYY"
                    style={{
                      width: "100%",
                      height: 44,
                      borderRadius: 10,
                      borderColor: R.border,
                    }}
                    placeholder={t("generateReport.selectDate")}
                    disabledDate={(current) =>
                      current && current.isAfter(dayjs(), "day")
                    }
                  />
                </div>

                {/* Собствен чекбокс, а не antd — antd рисува своя в синьо, а
                    „Маркиране" е изцяло в оранжевото `pos-accent`. Същият квадрат
                    като при добавките в модала „Промени". */}
                <button
                  type="button"
                  onClick={() => setIncludeSoldSellables((prev) => !prev)}
                  aria-pressed={includeSoldSellables}
                  className="flex cursor-pointer items-center gap-2.5 border-0 bg-transparent p-0 text-left text-[13px] text-pos-ink"
                >
                  <span
                    className={`grid h-[22px] w-[22px] flex-shrink-0 place-items-center rounded-[7px] border-2 ${
                      includeSoldSellables
                        ? "border-pos-accent bg-pos-accent text-white"
                        : "border-pos-check bg-pos-card"
                    }`}
                  >
                    {includeSoldSellables && (
                      <CheckOutlined className="text-[12px]" />
                    )}
                  </span>
                  {t(
                    usesWarehouse
                      ? "generateReport.showSoldRecipes"
                      : "generateReport.showSoldAssortments",
                  )}
                </button>
              </div>

              <ModalFooter isNarrow={isNarrow} narrowFirst>
                <GhostButton
                  label={t("common.cancel")}
                  onClick={onClose}
                  disabled={isGenerating}
                  shortcut={isDesktop ? "Esc" : undefined}
                />
                <PrimaryButton
                  label={t("generateReport.previewReport")}
                  onClick={handlePreviewReport}
                  disabled={!selectedUserId}
                  loading={isGenerating}
                  shortcut={isDesktop ? "⏎" : undefined}
                />
              </ModalFooter>
            </>
          )}
        </div>
      </Modal>
    </>
  );
};
